import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const tabyPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-taby",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Täby – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Täby. Vi hämtar möbler, vitvaror och skräp i Näsbypark, Gribbylund, Viggbyholm, Skarpäng, Roslags-Näsby och Täby Park. Fast pris, ofta samma dag.",
      h1: "Bortforsling Täby",
    },

    hero: {
      heading: "Bortforsling i Täby",
      intro:
        "Boka bortforsling i Täby – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från villorna i Näsbypark, Gribbylund och Viggbyholm till lägenheterna i Grindtorp och nya Täby Park. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Täby – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Täby",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Täby och körs till Hagby ÅVC",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Näsbypark, Täby",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Gribbylund, Täby",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Täby Park, Täby",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Viggbyholm, Täby",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Skarpäng, Täby",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Täby – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Täby är en av Stockholms mest villatäta kommuner och porten till Roslagen – med stora villaområden i Näsbypark, Gribbylund, Viggbyholm, Skarpäng, Ella Gård och Täby kyrkby. Undantagen är lägenhetskvarteren i Grindtorp och Näsbydal nära Täby centrum, samt nya Täby Park som växer fram på det gamla galoppfältet. Den blandningen gör bortforsling till olika utmaningar: i villan handlar det ofta om ett fullt garage, en vind eller ett källarförråd som ska tömmas, medan det i lägenheten snarare handlar om trapphus och hiss. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din villa, lägenhet eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Här har Täby en unik fördel – Hagby ÅVC, som ligger i kommunen vid Frestavägen, är SÖRAB:s största och mest besökta återvinningscentral. Eftersom Täby är delägare i SÖRAB och regionens största station ligger på hemmaplan behöver avfallet sällan köras långt. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E18 går genom Täby och Roslagsbanans alla tre grenar passerar kommunen – med stationer som Roslags-Näsby, Täby centrum, Galoppfältet, Viggbyholm och Hägernäs – tar vi oss snabbt mellan kommundelarna. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både de stora villatomterna i Näsbypark och de smala vägarna i Täby kyrkby – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Täby",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Näsbypark, Gribbylund, Viggbyholm eller Skarpäng är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Grindtorp, Roslags-Näsby eller Täby Park, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning. Det sparar tid jämfört med att leta plats på gatan.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Täbybo på Hagby ÅVC vid Frestavägen.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Täby",
          content:
            "Vi utför bortforsling i hela Täby kommun – från de etablerade villakvarteren till de helt nya stadsdelarna. Oavsett om du bor i en villa i Näsbypark, ett radhus i Gribbylund, en lägenhet i Grindtorp eller en nybyggd etta i Täby Park kan vi vara på plats, ofta redan samma dag.\n\nI de centrala delarna kring Täby centrum – Tibble, Grindtorp och Näsbydal – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära köpcentrumet och Roslagsbanans station. I villaområdena Näsbypark, Viggbyholm, Skarpäng, Ella Gård och Täby kyrkby är vi vana vid stora tomter, garage och förråd.\n\nVi är minst lika snabba i kommunens övriga delar: Gribbylund och Ensta i norr, Roslags-Näsby och Hägernäs längs Roslagsbanan, samt det växande Täby Park på det gamla galoppfältet och handelsområdet i Arninge med sin nya station. Tack vare närheten till E18, Norrortsleden och Roslagsbanan planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Täby",
          content:
            "En viktig del av vår bortforsling i Täby är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i området. Täby har en unik fördel eftersom kommunen är delägare i SÖRAB och dessutom rymmer regionens största station:\n\nHagby ÅVC vid Frestavägen i Täby – SÖRAB:s största och mest besökta återvinningscentral, där sorterat grovavfall, trädgårdsavfall, elavfall och farligt avfall tas emot, liksom föremål och textil för återbruk. SÖRAB:s mobila återvinningscentral – som ställs upp nära där du bor för mindre mängder. Eftersom Hagby ligger på hemmaplan håller vi restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Täby med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Täbys kommundelar, från villatomterna i Näsbypark och Viggbyholm till lägenhetskvarteren i Grindtorp och nya Täby Park, och vet att Hagby ÅVC ligger mitt i kommunen. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E18 går genom Täby. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Täby, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Hagby ÅVC och SÖRAB:s anläggningar. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Täby?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Täby.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Täby?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Täby?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och E18 går genom Täby, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Täby?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Hämtar ni från villa och garage i Täby?",
        answer:
          "Ja, en stor del av våra uppdrag i Täby är villahämtningar i Näsbypark, Gribbylund, Viggbyholm, Skarpäng och Täby kyrkby. Vi backar in på uppfarten där det går och tömmer garage, källare, vind och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Täby?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Grindtorp, Näsbydal och Täby Park. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Täby försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Täby?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. I Täby ligger SÖRAB:s största station, Hagby ÅVC vid Frestavägen, mitt i kommunen. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Täby på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare, vind eller förråd i Täby?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, vindsutrymmen, förråd och garage. Vi är vana vid de stora villaförråden i Näsbypark och Gribbylund såväl som lägenhetsförråden i Grindtorp.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Täby?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din villa i Viggbyholm och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka kommundelar i Täby täcker ni?",
        answer:
          "Vi utför bortforsling i hela Täby – Täby centrum, Tibble, Grindtorp, Näsbydal, Näsbypark, Roslags-Näsby, Gribbylund, Viggbyholm, Skarpäng, Ella Gård, Täby kyrkby, Hägernäs, Arninge och Täby Park. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Täby?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som boende i Täby på Hagby ÅVC vid Frestavägen.",
      },
      {
        question: "Vilka kommuner runt Täby utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Täby i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Täby kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Täby idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Täby kommun.",
    },

    en: {
      slug: "junk-removal-taby",
      meta: {
        title: "Junk Removal Täby – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Täby. We pick up furniture, appliances and bulky items in Näsbypark, Gribbylund, Viggbyholm, Skarpäng, Roslags-Näsby and Täby Park. Fixed price, often same day.",
        h1: "Junk Removal Täby",
      },
      hero: {
        heading: "Junk Removal in Täby",
        intro:
          "Book junk removal in Täby – we collect furniture, appliances and bulky items across the whole municipality, from the villas in Näsbypark, Gribbylund and Viggbyholm to the apartments in Grindtorp and the new Täby Park. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Täby – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Täby",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Täby and driven to Hagby recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Näsbypark, Täby",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Gribbylund, Täby",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Täby Park, Täby",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Viggbyholm, Täby",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Skarpäng, Täby",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Täby – fast, hassle-free and responsible",
        mainText:
          "Täby is one of Stockholm's most villa-dense municipalities and the gateway to Roslagen – with large villa areas in Näsbypark, Gribbylund, Viggbyholm, Skarpäng, Ella Gård and Täby kyrkby. The exceptions are the apartment quarters of Grindtorp and Näsbydal near Täby centrum, and the new Täby Park rising on the old galopp racecourse. That mix makes junk removal a different challenge: in a villa it is often a full garage, an attic or a basement storage room to be cleared, while in an apartment it is more about stairwells and elevators. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your villa, apartment or storage unit, load them into the van and drive to the nearest recycling centre. Here Täby has a unique advantage – Hagby ÅVC, located in the municipality on Frestavägen, is SÖRAB's largest and most-visited recycling centre. Because Täby is a part-owner of SÖRAB and the region's biggest station is right at home, the waste rarely has to be driven far. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E18 runs through Täby and all three branches of the Roslagsbanan railway pass through the municipality – with stations such as Roslags-Näsby, Täby centrum, Galoppfältet, Viggbyholm and Hägernäs – we move quickly between districts. This means we can often be on site the same day you book. Our workers are used to both the large villa plots of Näsbypark and the narrow roads of Täby kyrkby – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Täby",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Näsbypark, Gribbylund, Viggbyholm or Skarpäng, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Grindtorp, Roslags-Näsby or Täby Park, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use. It saves time compared to searching for a spot on the street.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Täby resident you can drop those off for free at Hagby recycling centre on Frestavägen.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Täby",
            content:
              "We perform junk removal across the entire municipality of Täby – from the established villa neighbourhoods to the brand-new districts. Whether you live in a villa in Näsbypark, a townhouse in Gribbylund, an apartment in Grindtorp or a new studio in Täby Park, we can be on site, often the same day.\n\nIn the central parts around Täby centrum – Tibble, Grindtorp and Näsbydal – we are highly experienced with apartment pickups via elevator and stairwell, close to the shopping centre and the Roslagsbanan station. In the villa areas of Näsbypark, Viggbyholm, Skarpäng, Ella Gård and Täby kyrkby we are used to large plots, garages and storage rooms.\n\nWe are just as quick in the other parts of the municipality: Gribbylund and Ensta to the north, Roslags-Näsby and Hägernäs along the Roslagsbanan, and the growing Täby Park on the old racecourse and the Arninge retail area with its new station. Thanks to the proximity of the E18, Norrortsleden and the Roslagsbanan, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Täby",
            content:
              "An important part of our junk removal in Täby is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the area. Täby has a unique advantage because the municipality is a part-owner of SÖRAB and is also home to the region's largest station:\n\nHagby ÅVC on Frestavägen in Täby – SÖRAB's largest and most-visited recycling centre, which receives sorted bulky waste, garden waste, e-waste and hazardous waste, as well as items and textiles for reuse. SÖRAB's mobile recycling centre – set up near where you live for smaller amounts. Because Hagby is right at home, we keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Täby with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Täby's districts, from the villa plots of Näsbypark and Viggbyholm to the apartment quarters of Grindtorp and the new Täby Park, and that Hagby ÅVC is right in the middle of the municipality. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E18 runs through Täby. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Täby, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Hagby ÅVC and SÖRAB's facilities. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Täby cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Täby.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Täby?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Täby?",
          answer:
            "We have vans and staff across the Stockholm region and the E18 runs through Täby, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Täby?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Do you collect from villas and garages in Täby?",
          answer:
            "Yes, a large share of our jobs in Täby are villa pickups in Näsbypark, Gribbylund, Viggbyholm, Skarpäng and Täby kyrkby. We reverse onto the driveway where possible and clear garages, basements, attics and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Täby?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Grindtorp, Näsbydal and Täby Park. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Täby insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Täby?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Täby is home to SÖRAB's largest station, Hagby ÅVC on Frestavägen, right in the middle of the municipality. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Täby on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements, attics or storage units in Täby?",
          answer:
            "Yes, we pick up items from all spaces – basements, attics, storage rooms and garages. We are used to the large villa storage rooms in Näsbypark and Gribbylund as well as the apartment storage units in Grindtorp.",
        },
        {
          question: "Can you make multiple stops during junk removal in Täby?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your villa in Viggbyholm and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which districts in Täby do you cover?",
          answer:
            "We perform junk removal across all of Täby – Täby centrum, Tibble, Grindtorp, Näsbydal, Näsbypark, Roslags-Näsby, Gribbylund, Viggbyholm, Skarpäng, Ella Gård, Täby kyrkby, Hägernäs, Arninge and Täby Park. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Täby?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Täby resident you can drop those off for free at Hagby recycling centre on Frestavägen.",
        },
        {
          question: "Which municipalities around Täby do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Täby in 3 easy steps",
        step2Text:
          "Our young workers in Täby come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Täby today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Täby.",
      },
    },
  },
];
